/**
 * Kaching Bundles – Selection Tracker
 * -----------------------------------
 * Liest bei Load und bei jeder Änderung aus, was der Kunde im Bundle-Block gewählt hat.
 *
 * Feuert auf document:
 *   'kaching:selection'  -> { detail: { reason, blockId, state } }
 *
 * Manuell auslesen:
 *   window.kachingReadSelection()          // Array aller Blöcke
 *   window.kachingReadSelection(blockEl)   // ein Block
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 120;
  var BLOCK_TAG = 'kaching-bundles-block';
  var COLOR_OPTION_NAME = 'farbe';

  /* ---------------------------------------------------------------- utils */

  function parseAttr(el, name) {
    var raw = el.getAttribute(name);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function handleFromHref(href) {
    if (!href) return null;
    try {
      var m = new URL(href, location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  function variantIdFromHref(href) {
    if (!href) return null;
    try {
      var v = new URL(href, location.origin).searchParams.get('variant');
      return v ? Number(v) : null;
    } catch (e) { return null; }
  }

  /* ------------------------------------------------------- produkt-index */

  function buildProductIndex(block) {
    var main = parseAttr(block, 'product');
    var sources = []
      .concat(main ? [main] : [])
      .concat(parseAttr(block, 'collection-breaks-products') || [])
      .concat(parseAttr(block, 'complementary-products') || [])
      .concat(parseAttr(block, 'other-products') || []);

    var byHandle = {};
    var byVariantId = {};

    sources.forEach(function (p) {
      if (!p || !p.handle) return;
      if (!byHandle[p.handle]) byHandle[p.handle] = p;
      (p.variants || []).forEach(function (v) {
        byVariantId[v.id] = { product: p, variant: v };
      });
    });

    return {
      byHandle: byHandle,
      byVariantId: byVariantId,
      main: main,
      // In Reihenfolge, wie Kaching sie ausliefert: Upsells mit
      // productSource "complementary" ziehen genau daraus.
      complementary: parseAttr(block, 'complementary-products') || []
    };
  }

  /**
   * Optionswerte -> Variante.
   * Die <select>s stehen in Options-Reihenfolge (position 1..n),
   * variant.options ist in derselben Reihenfolge.
   */
  function findVariant(product, values) {
    if (!product || !product.variants || !values.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (!v.options || v.options.length !== values.length) continue;
      var ok = true;
      for (var j = 0; j < values.length; j++) {
        if (v.options[j] !== values[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /**
   * Variante ueber die Options-NAMEN statt ueber die Position.
   *
   * findVariant() verlangt, dass die Zeile alle Optionen des Produkts zeigt.
   * Eine Upsell-Zeile zeigt aber oft nur "Farbe", waehrend das Produkt
   * zusaetzlich eine Groesse hat – dann schlug die Laengenpruefung fehl und es
   * gab gar keine Variante. Hier zaehlen nur die Optionen, die die Zeile
   * tatsaechlich anbietet; der Rest bleibt offen und die erste passende
   * Variante gewinnt (bei gleicher Farbe teilen sie sich ohnehin das Bild).
   */
  function findVariantByOptionNames(product, opts) {
    if (!product || !product.variants || !opts.length) return null;

    var names = (product.options || []).map(function (o) {
      return String((o && o.name) || o || '').trim().toLowerCase();
    });

    var wanted = [];
    opts.forEach(function (o) {
      if (!o.value || !o.name) return;
      var pos = names.indexOf(String(o.name).trim().toLowerCase());
      if (pos >= 0) wanted.push([pos, o.value]);
    });
    if (!wanted.length) return null;

    /*
     * Kennt das Produkt eine Farb-Option, muss sie unter den gepinnten sein.
     * Sonst liefert der Teiltreffer die erste Variante mit passender Groesse –
     * also irgendeine Farbe. Lieber gar keine Variante als die falsche Farbe:
     * das Modul faellt dann auf die Rollen-Map zurueck, die die Farbe kennt.
     */
    var colorPos = names.indexOf(COLOR_OPTION_NAME);
    if (colorPos >= 0) {
      var colorPinned = wanted.some(function (w) { return w[0] === colorPos; });
      if (!colorPinned) return null;
    }

    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (!v.options) continue;
      var ok = true;
      for (var j = 0; j < wanted.length; j++) {
        if (v.options[wanted[j][0]] !== wanted[j][1]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /**
   * Name + Wert eines Options-Controls.
   *
   * Bei swatchType "productImageDropdown" ist das Control ein
   * <span role="combobox">, kein <select> – .value und .dataset.value sind dort
   * leer. Der Wert steht am umschliessenden .kaching-bundles__swatch-dropdown
   * als data-value, notfalls im Text des inneren <span>.
   *
   * Ohne diesen Zweig fehlte die Farbe in den Optionswerten, und die
   * Variantensuche fiel auf die erste Variante mit passender Groesse zurueck –
   * im Modul erschien dann dauerhaft die erste Farbe des Produkts.
   */
  function readOption(sel) {
    var wrap = sel.closest('.kaching-bundles__swatch-dropdown');

    var name =
      sel.dataset.name ||
      sel.getAttribute('aria-label') ||
      (wrap && wrap.dataset.name) ||
      null;

    var value = sel.value || sel.dataset.value || (wrap && wrap.dataset.value) || null;
    if (!value && sel.tagName !== 'SELECT') {
      // Letzter Ausweg: der sichtbare Options-Name im Combobox-Text.
      var label = sel.querySelector('span');
      var text = (label ? label.textContent : sel.textContent) || '';
      value = text.trim() || null;
    }

    return { name: name, value: value };
  }

  /** Optionswerte aus einem Scope lesen – Selects ODER Swatches. */
  function readOptions(scope) {
    var out = [];

    scope.querySelectorAll('.kaching-bundles__bar-variant-select').forEach(function (sel) {
      if (sel.closest('.kaching-bundles__collection-product, .kaching-bundles__upsell') !== scope.closest('.kaching-bundles__collection-product, .kaching-bundles__upsell')) return;
      out.push(readOption(sel));
    });

    if (!out.length) {
      // Swatch-Variante (falls im Theme aktiviert)
      scope.querySelectorAll('.kaching-bundles__swatches').forEach(function (group) {
        var active = group.querySelector('[class*="--selected"], [aria-checked="true"], .kaching-bundles__swatch--active');
        out.push({
          name: group.dataset.name || group.getAttribute('aria-label') || null,
          value: active ? (active.dataset.value || active.getAttribute('aria-label')) : null
        });
      });
    }

    return out;
  }

  /** Heuristik: ist ein Upsell/Checkbox aktiv? */
  function isChecked(el) {
    if (!el) return false;
    if (/(--selected|--checked|is-selected|is-checked|--active)/.test(el.className || '')) return true;
    if (el.querySelector('input[type="checkbox"]:checked')) return true;
    var box = el.querySelector('.kaching-bundles__upsell__checkbox, .kaching-bundles__free-gift__checkbox');
    if (box) {
      if (/(--selected|--checked|--active)/.test(box.className || '')) return true;
      // im ungewählten Zustand ist die Box leer, im gewählten enthält sie ein Häkchen
      if (box.children.length > 0) return true;
    }
    return false;
  }

  /* ---------------------------------------------------------- state read */

  /**
   * Produktbild aus dem Kaching-Produkt-JSON.
   *
   * Kaching serialisiert das Bild als STRING unter `image`
   * ("//shop.de/cdn/...jpg?height=200"), nicht als featured_image/images wie
   * Shopifys `product | json`. Beides pruefen, weil dieselbe Funktion auch
   * Produkte aus Theme-JSON sieht.
   */
  function productImage(product) {
    if (!product) return null;
    if (product.image) return product.image;
    if (product.featured_image) return product.featured_image;
    if (product.images && product.images.length) return product.images[0];
    return null;
  }

  /**
   * Letzte Rueckfallebene fuer das Bild: das, was Kaching in der Zeile selbst
   * rendert. Bei swatchType "productImageDropdown" ist das sogar das Bild der
   * gewaehlten Farbe. Kann fehlen – Upsells mit imageSize 0 rendern kein Bild.
   */
  function domImage(el) {
    // Die Klasse haengt am <img> selbst, nicht an einem Container.
    var img = el.querySelector(
      'img.kaching-bundles__upsell__image[src], img.kaching-bundles__collection-product__image[src], img[src]'
    );
    return img ? img.getAttribute('src') : null;
  }

  function readItem(el, idx, index) {
    var link = el.querySelector('a.kaching-bundles__collection-product__link[href], a.kaching-bundles__upsell__link[href]');
    var href = link ? link.getAttribute('href') : null;
    var handle = handleFromHref(href);

    /*
     * Aufloesung ueber die Variant-ID, NICHT ueber den Handle.
     *
     * Kaching baut den Upsell-Link teils aus der URL des Hauptprodukts plus dem
     * ?variant= des Upsells (z. B. /products/lyocell-bettwaesche?variant=<Laken>).
     * Der Handle zeigt dann aufs Hauptprodukt, die Variant-ID aufs richtige –
     * Ergebnis war der PDP-Titel am Spannbettlaken-Upsell. Shopify-Handles sind
     * eindeutig, die Variant-ID ist aber die praezisere Angabe, also zuerst sie.
     */
    var vid = variantIdFromHref(href);
    var hit = vid ? index.byVariantId[vid] : null;
    var isUpsell = !!(el.classList && el.classList.contains('kaching-bundles__upsell'));

    var product = null;
    var resolvedVia = null;

    if (hit) {
      product = hit.product;
      resolvedVia = 'variant-id';
    } else if (handle && index.byHandle[handle]) {
      product = index.byHandle[handle];
      resolvedVia = 'handle';
    } else if (isUpsell) {
      /*
       * NICHT auf index.main zurueckfallen. Eine Upsell-Zeile zeigt nie das
       * Hauptprodukt – tat sie es doch, landete dessen Bild im Modul (zuletzt
       * Packshot_Forest_Green_Main.jpg am Spannbettlaken).
       *
       * Die Upsells dieses Blocks haben productSource "complementary" und
       * keine productGID, ziehen also der Reihe nach aus complementary-products.
       * Das ist eine Zuordnung ueber die Position – wenn Kaching die Reihenfolge
       * aendert, greift sie daneben. Verlaesslich ist nur das im Liquid
       * hinterlegte Rollen-Produkt (data-roles-json).
       */
      if (index.complementary[idx]) {
        product = index.complementary[idx];
        resolvedVia = 'complementary-index';
      }
    } else {
      // Erste Produktzeile ohne Link = das aktuelle Produkt der PDP.
      product = index.main;
      resolvedVia = 'main';
    }

    var opts = readOptions(el);
    var values = opts.map(function (o) { return o.value; }).filter(Boolean);

    // Die im Picker gewaehlten Optionen schlagen den Link: wechselt der Kunde
    // die Farbe, ist der href u. U. noch der alte.
    var variant =
      findVariant(product, values) ||
      findVariantByOptionNames(product, opts) ||
      (hit ? hit.variant : null);

    return {
      position: idx,
      productId: product ? product.id : null,
      productHandle: product ? product.handle : handle,
      productTitle: product ? product.title : null,
      // type + Bild mitgeben: <tt-0028> erkennt die Bundle-Rolle ueber den
      // Produkttyp und braucht ein Bild, wenn das Metafeld keins liefert.
      productType: product ? (product.type || null) : null,
      productImage: product ? productImage(product) : null,
      variantId: variant ? variant.id : null,
      variantTitle: variant ? variant.options.join(' / ') : values.join(' / '),
      variantSku: variant ? (variant.sku || null) : null,
      // Das Variantenbild trifft die gewaehlte Farbe per Definition – damit
      // braucht <tt-0028> fuer das Upsell keine eigene Farbzuordnung.
      variantImage: variant ? (variant.image || null) : null,
      domImage: domImage(el),
      // Woher das Produkt kam – im Dump die entscheidende Spalte.
      resolvedVia: resolvedVia,
      isUpsell: isUpsell,
      options: opts,
      price: variant ? variant.price / 100 : null,
      available: variant ? variant.availableForSale : null
    };
  }

  function readState(block) {
    var root = block.querySelector('.kaching-bundles__block');
    if (!root) return null;

    var cfg = parseAttr(block, 'deal-block') || {};
    var index = buildProductIndex(block);

    var checked = root.querySelector('.kaching-bundles__bars input[type="radio"]:checked');
    var barEl = (checked && checked.closest('.kaching-bundles__bar')) ||
                root.querySelector('.kaching-bundles__bar--selected');
    if (!barEl) return null;

    var barId = barEl.dataset.dealBarId || (checked && checked.value) || null;
    var barCfg = (cfg.dealBars || []).filter(function (b) { return b.id === barId; })[0] || null;

    // Wie viele Artikel sind bezahlt (Rest = gratis)?
    var paidQty = barCfg
      ? (barCfg.dealBarType === 'bxgy' ? (barCfg.buyQuantity || 1) : (barCfg.quantity || 1))
      : 1;

    /*
     * Wie viele Artikel bekommt der Kunde insgesamt? Bei bxgy sind das die
     * bezahlten PLUS die gratis dazu – "Kaufe 1, erhalte 1 gratis" liefert zwei
     * Sets. paidQty allein hat in der Fallback-Schleife unten nur eines
     * erzeugt, entsprechend zeigte <tt-0028> die halbe Menge.
     */
    var totalQty = barCfg && barCfg.dealBarType === 'bxgy'
      ? paidQty + (barCfg.getQuantity || 0)
      : paidQty;

    /* --- gewählte Produkte --- */
    var items = [];
    var productEls = barEl.querySelectorAll('.kaching-bundles__bar-collection-products .kaching-bundles__collection-product');

    if (productEls.length) {
      productEls.forEach(function (el, i) {
        var item = readItem(el, i, index);
        item.isFree = barCfg && barCfg.dealBarType === 'bxgy' ? i >= paidQty : false;
        item.chosen = !!el.querySelector('.kaching-bundles__collection-product__remove-button') || i === 0;
        items.push(item);
      });
    } else {
      // Bar ohne eigenen Variantenpicker (z. B. Einzelset / Quantity-Break):
      // die Variante kommt aus dem Theme-Formular.
      var vid = Number(block.getAttribute('current-variant-id')) || null;
      var form = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (form && form.value) vid = Number(form.value);

      var hit = vid ? index.byVariantId[vid] : null;
      // Ohne eigenen Picker gilt fuer alle Artikel die Variante aus dem
      // Theme-Formular – die Gratis-Bettwaesche waehlt der Kunde erst im Modal.
      for (var q = 0; q < totalQty; q++) {
        items.push({
          position: q,
          productId: hit ? hit.product.id : (index.main ? index.main.id : null),
          productHandle: hit ? hit.product.handle : (index.main ? index.main.handle : null),
          productTitle: hit ? hit.product.title : (index.main ? index.main.title : null),
          productType: hit ? (hit.product.type || null) : (index.main ? (index.main.type || null) : null),
          productImage: productImage(hit ? hit.product : index.main),
          variantId: vid,
          variantTitle: hit ? hit.variant.options.join(' / ') : null,
          variantSku: hit ? (hit.variant.sku || null) : null,
          variantImage: hit ? (hit.variant.image || null) : null,
          domImage: null,
          resolvedVia: hit ? 'variant-id' : 'main',
          isUpsell: false,
          options: hit ? hit.variant.options.map(function (v, i2) {
            return { name: hit.product.options[i2] ? hit.product.options[i2].name : null, value: v };
          }) : [],
          price: hit ? hit.variant.price / 100 : null,
          available: hit ? hit.variant.availableForSale : null,
          isFree: q >= paidQty,
          chosen: true
        });
      }
    }

    /* --- Upsells / Free Gifts --- */
    var upsells = [];
    barEl.querySelectorAll('.kaching-bundles__upsell').forEach(function (el, i) {
      var item = readItem(el, i, index);
      var upsellCfg = ((barCfg && barCfg.upsells) || []).filter(function (u) {
        return u.id === el.dataset.upsellId;
      })[0] || null;
      upsells.push({
        upsellId: el.dataset.upsellId || null,
        selected: isChecked(el),
        discountType: upsellCfg ? upsellCfg.discountType : null,
        discountValue: upsellCfg ? upsellCfg.discountValue : null,
        product: item
      });
    });

    /* --- Preise aus der UI (was der Kunde sieht) --- */
    var priceEl = barEl.querySelector('.kaching-bundles__bar-price');
    var fullEl = barEl.querySelector('.kaching-bundles__bar-full-price');

    return {
      blockId: block.getAttribute('deal-block-id'),
      abTestVariant: (root.dataset.abTestVariant || null),
      bar: {
        id: barId,
        type: barCfg ? barCfg.dealBarType : null,        // 'bxgy' | 'quantity-break' | ...
        title: barCfg ? barCfg.title : (barEl.querySelector('.kaching-bundles__bar-title') || {}).textContent,
        buyQuantity: barCfg ? (barCfg.buyQuantity || barCfg.quantity || 1) : 1,
        getQuantity: barCfg ? (barCfg.getQuantity || 0) : 0,
        discountType: barCfg ? (barCfg.getDiscountType || barCfg.discountType) : null,
        discountValue: barCfg ? (barCfg.getDiscountValue != null ? barCfg.getDiscountValue : barCfg.discountValue) : null,
        displayPrice: priceEl ? priceEl.textContent.trim() : null,
        displayComparePrice: fullEl ? fullEl.textContent.trim() : null
      },
      items: items,
      upsells: upsells,
      // alles, was tatsächlich in den Warenkorb wandert:
      variantIds: items.map(function (i) { return i.variantId; })
        .concat(upsells.filter(function (u) { return u.selected; })
          .map(function (u) { return u.product.variantId; }))
        .filter(Boolean)
    };
  }

  /* ------------------------------------------------------------- watcher */

  function watch(block) {
    if (block.__kachingWatched) return;
    block.__kachingWatched = true;

    var last = null;
    var timer = null;

    function emit(reason) {
      var state = readState(block);
      if (!state) return;
      var json = JSON.stringify(state);
      if (json === last) return;          // nur echte Änderungen melden
      last = json;

      document.dispatchEvent(new CustomEvent('kaching:selection', {
        detail: { reason: reason, blockId: state.blockId, state: state }
      }));
    }

    function schedule(reason) {
      clearTimeout(timer);
      timer = setTimeout(function () { emit(reason); }, DEBOUNCE_MS);
    }

    // Delegiert am Custom Element – überlebt jedes Re-Render der Vue-App
    block.addEventListener('change', function () { schedule('change'); }, true);
    block.addEventListener('click',  function () { schedule('click');  }, true);

    // Programmatische Änderungen (Auto-Fill, Varianten-Sync, Modal-Ergebnis)
    new MutationObserver(function () { schedule('mutation'); }).observe(block, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'value', 'data-value', 'href', 'current-variant-id', 'data-deal-bar-id']
    });

    // "Wähle deine Gratis Bettwäsche"-Modal wird außerhalb des Blocks gerendert
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[class*="kaching-bundles__choose-product"], [class*="kaching-bundles__modal"]')) {
        schedule('modal');
      }
    }, true);

    // Theme-Variantenwechsel (relevant für den Einzelkauf-Bar)
    document.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest('form[action*="/cart/add"]')) schedule('theme-variant');
    }, true);

    schedule('init');
  }

  function boot(block) {
    if (block.querySelector('.kaching-bundles__block--loaded')) return watch(block);
    // auf das Rendern der App warten
    var mo = new MutationObserver(function () {
      if (block.querySelector('.kaching-bundles__block--loaded')) {
        mo.disconnect();
        watch(block);
      }
    });
    mo.observe(block, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }

  function init() {
    document.querySelectorAll(BLOCK_TAG).forEach(boot);
    // Blöcke, die später nachgeladen werden (Section Rendering API)
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.tagName && n.tagName.toLowerCase() === BLOCK_TAG) boot(n);
          else if (n.querySelectorAll) n.querySelectorAll(BLOCK_TAG).forEach(boot);
        });
      });
    }).observe(document.documentElement, { subtree: true, childList: true });
  }

  window.kachingReadSelection = function (block) {
    if (block) return readState(block);
    return Array.prototype.map.call(document.querySelectorAll(BLOCK_TAG), readState).filter(Boolean);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* =====================================================================
   VERWENDUNG
   =====================================================================

document.addEventListener('kaching:selection', function (e) {
  var s = e.detail.state;

  console.log('Auslöser:', e.detail.reason);          // init | change | click | mutation | modal
  console.log('Angebot :', s.bar.type, s.bar.title);  // z.B. 'bxgy' / 'Kaufe 1, erhalte 1 Gratis'
  console.log('Produkte:', s.items);                  // inkl. isFree-Flag
  console.log('Upsells :', s.upsells.filter(u => u.selected));
  console.log('Varianten-IDs:', s.variantIds);

  // Beispiel: nur beim Wechsel des Angebots tracken
  // if (e.detail.reason !== 'init') dataLayer.push({ event: 'bundle_change', bundle: s.bar.type });
});

   ===================================================================== */

/**
 * Kaching Bundles – Upsell-Farbe an die PDP-Farbe koppeln
 * ------------------------------------------------------
 * Das "Passendes Spannbettlaken"-Upsell ist als productSource:"complementary"
 * konfiguriert. Kaching gleicht Options-Werte nur INNERHALB der Deal-Bar-Produkte
 * ab (disableVariantOptionSync), nicht zwischen Hauptprodukt und Upsell – der
 * Shop-Flag "propagate_picker_option_changes" ist aus. Eine Einstellung dafuer
 * gibt es nicht, das machen wir hier selbst.
 *
 * Die Farbe rendert Kaching als swatchType "productImageDropdown", also KEIN
 * natives <select>: ein sel.value = "..." laeuft ins Leere, die App reagiert
 * nur auf ihre eigenen Click-Handler. Wir klicken deshalb die Combobox auf und
 * danach die Option mit passendem data-value (= exakter Options-Name).
 * Bettwaesche und Spannbettlaken benutzen dieselben 12 Farbnamen, ein Mapping
 * ist daher nicht noetig.
 *
 * Synchronisiert wird NUR bei echtem Farbwechsel am Hauptprodukt sowie einmalig
 * beim ersten Render. Waehlt der Kunde bewusst eine andere Laken-Farbe, bleibt
 * die stehen.
 */
(function () {
  'use strict';

  var COLOR_OPTION = 'farbe';
  var MAX_RETRIES = 4;      // gegen Kaching-Re-Renders, die unseren Klick ueberschreiben
  var RETRY_MS = 200;
  var MAX_FRAMES = 30;      // ~500ms warten, bis die Options-Liste gerendert ist

  /**
   * Die Options-Liste ist position:fixed mit Box-Shadow – beim programmatischen
   * Auf-/Zuklicken wuerde sie sonst kurz aufblitzen. Die Regel lebt bewusst hier
   * statt in custom.css: sie maskiert ausschliesslich einen Zustand, den dieses
   * Script erzeugt, und soll nicht getrennt davon verwaltet werden.
   */
  var open = 0;
  var style = document.createElement('style');
  style.textContent =
    '[data-tt-kaching-sync] .kaching-bundles__swatch-dropdown__options{visibility:hidden !important}';
  (document.head || document.documentElement).appendChild(style);

  function maskOn() {
    if (open++ === 0) document.documentElement.setAttribute('data-tt-kaching-sync', '');
  }

  function maskOff() {
    if (--open <= 0) {
      open = 0;
      document.documentElement.removeAttribute('data-tt-kaching-sync');
    }
  }

  /** Farb-Control eines Upsells – Swatch-Combobox (<span>) oder natives <select>. */
  function colorControl(upsell) {
    var els = upsell.querySelectorAll('.kaching-bundles__bar-variant-select');
    for (var i = 0; i < els.length; i++) {
      var name = els[i].getAttribute('aria-label') || els[i].dataset.name || '';
      if (name.trim().toLowerCase() === COLOR_OPTION) return els[i];
    }
    return null;
  }

  function matches(el, color) {
    if (el.tagName === 'SELECT') return el.value === color;
    // Combobox-Text ist der Options-Name, bei nicht verfuegbaren Werten mit Suffix.
    return (el.textContent || '').trim().indexOf(color) === 0;
  }

  /**
   * Kaching fokussiert nach der Auswahl die Combobox zurueck:
   *   function H(v) { i(v), G(); ... }   G() -> combobox.focus()
   * Bei einem echten Klick ist das richtig (Tastatur-Bedienung), bei unserem
   * programmatischen Klick scrollt focus() die Kaching-Box aber in den Viewport
   * und die Seite springt. Wir zwingen focus() daher fuer die Dauer des Klicks
   * auf preventScroll. Die Patch-Dauer ist ein synchroner Klick, echte
   * Nutzer-Interaktionen sind nicht betroffen.
   */
  function clickWithoutScroll(option, combobox) {
    var hadOwn = Object.prototype.hasOwnProperty.call(combobox, 'focus');
    var prev = combobox.focus;
    var x = window.scrollX;
    var y = window.scrollY;

    combobox.focus = function () {};

    try {
      option.click();
    } finally {
      setTimeout(function () {
        if (hadOwn) combobox.focus = prev;
        else delete combobox.focus;
      }, 0);
    }

    if (document.activeElement === combobox || combobox.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    // Fallback, falls preventScroll ignoriert wird: der Klick laeuft synchron
    // ab, zwischen Sprung und Korrektur liegt also kein Paint.
    if (window.scrollX !== x || window.scrollY !== y) {
      window.scrollTo({ top: y, left: x, behavior: 'auto' });
    }
  }

  /** Klickt die Option mit passendem data-value, sobald die Liste gerendert ist. */
  function pick(wrap, combobox, color, frame) {
    var opts = wrap.querySelectorAll('.kaching-bundles__swatch-dropdown__option');
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].dataset.value === color) {
        clickWithoutScroll(opts[i], combobox);
        return void maskOff();
      }
    }
    if (opts.length) {
      // Liste steht, Farbe ist aber nicht dabei (hideUnavailableOptions) -> wieder zu.
      if (combobox.getAttribute('aria-expanded') === 'true') combobox.click();
      return void maskOff();
    }
    if (frame < MAX_FRAMES) {
      requestAnimationFrame(function () { pick(wrap, combobox, color, frame + 1); });
      return;
    }
    maskOff();
  }

  function apply(el, color) {
    if (el.tagName === 'SELECT') {
      el.value = color;
      el.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    maskOn();
    if (el.getAttribute('aria-expanded') !== 'true') el.click();
    pick(el.closest('.kaching-bundles__swatch-dropdown') || el.parentNode, el, color, 0);
  }

    var lastColors = [];
  var cycle = null;     // Zielfarben des laufenden Durchlaufs, null = nicht anfassen
  var timer = null;
  var retries = 0;
  var seeded = false;

  function currentColor(el) {
    if (el.tagName === 'SELECT') return el.value;
    return (el.textContent || '').trim();
  }

  /** Farben der Bettwaesche-Zeilen (#1, #2, ...) in Reihenfolge des DOM. */
  function productColors() {
    var out = [];
    document.querySelectorAll('.kaching-bundles__bar-variant-select').forEach(function (el) {
      if (el.closest('.kaching-bundles__upsell')) return;
      var name = el.getAttribute('aria-label') || el.dataset.name || '';
      if (name.trim().toLowerCase() !== COLOR_OPTION) return;
      out.push(currentColor(el));
    });
    return out;
  }

  function upsellControls() {
    var out = [];
    document.querySelectorAll('.kaching-bundles__upsell').forEach(function (upsell) {
      var el = colorControl(upsell);
      if (el) out.push(el);
    });
    return out;
  }

  function tick() {
    var ups = upsellControls();
    var cols = productColors();

    if (!ups.length || !cols.length) {
      if (retries++ < MAX_RETRIES) timer = setTimeout(tick, RETRY_MS);
      else timer = null;
      return;
    }

    // Zielfarben einmal pro Durchlauf festlegen, damit Retries nicht neu rechnen.
    if (!cycle) {
      cycle = [];
      for (var i = 0; i < ups.length; i++) {
        // Weniger Produkte als Upsells (Einzelprodukt-Deal): alle folgen dem letzten.
        var src = Math.min(i, cols.length - 1);
        cycle.push(!seeded || lastColors[src] !== cols[src] ? cols[src] : null);
      }
      lastColors = cols;
      seeded = true;
    }

    var pending = 0;
    for (var j = 0; j < ups.length; j++) {
      if (!cycle[j] || matches(ups[j], cycle[j])) continue;
      pending++;
      apply(ups[j], cycle[j]);
    }

    if (pending && retries++ < MAX_RETRIES) timer = setTimeout(tick, RETRY_MS);
    else timer = null;
  }

  function schedule(delay) {
    clearTimeout(timer);
    cycle = null;
    retries = 0;
    timer = setTimeout(tick, delay);
  }

  document.addEventListener('twentythree:color-changed', function () {
    schedule(50);
  });

  // Faengt auch Farbwechsel ab, die direkt in der Deal Bar passieren (#2).
  document.addEventListener('kaching:selection', function (e) {
    if (cycle && timer) return;  // eigener Klick, laufenden Durchlauf nicht abwuergen
    schedule(e.detail && e.detail.reason === 'init' ? 0 : 50);
  });
})();
